// SKU package grouping, shared between the Orders grid and the
// Forecast-vs-Orders comparison.
//
// A SKU name is "Flavor / Size / Pack", so its package group is "Size / Pack".
// Group ORDER follows the account's own product_sizes / pack_sizes sort order
// (System Settings → Product SKUs); anything the account hasn't defined sorts
// last, alphabetically. There is no built-in list of sizes or packs — an
// earlier version hardcoded one brand's formats, which left every other tenant
// with a single "Other" bucket.

const OTHER = "Other";

function parts(skuName) {
  return String(skuName || "").split(" / ");
}

/** The pack segment of a "Flavor / Size / Pack" SKU name. */
export function getSkuPkg(skuName) {
  const p = parts(skuName);
  return p.length >= 3 ? p[2] : "";
}

/** The size segment of a "Flavor / Size / Pack" SKU name. */
export function getSkuSize(skuName) {
  const p = parts(skuName);
  return p.length >= 2 ? p[1] : "";
}

/** Package group label for a SKU — "Size / Pack", or "Other" if unparseable. */
export function getSkuGroup(skuName) {
  const p = parts(skuName);
  return p.length >= 3 ? `${p[1]} / ${p[2]}` : OTHER;
}

/** Index in `order`, or a value that sorts after everything known. */
function rankIn(order, value) {
  const i = (order || []).indexOf(value);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * Sort key for a SKU: size band first, then pack, then name.
 * @param {string} skuName
 * @param {{sizeOrder?: string[], packOrder?: string[]}} order the account's own ordering
 */
export function pkgRank(skuName, { sizeOrder = [], packOrder = [] } = {}) {
  return [rankIn(sizeOrder, getSkuSize(skuName)), rankIn(packOrder, getSkuPkg(skuName))];
}

/**
 * Sorted so every "Size / Pack" group is CONTIGUOUS — the grid draws a group
 * header whenever the group changes from the previous row, so a sort that
 * interleaves groups prints the same header over and over.
 *
 * Rank alone can't guarantee that: anything the account hasn't ordered ranks
 * MAX_SAFE_INTEGER, so unknown sizes (or every pack, when packOrder is empty)
 * tie and fall through to the SKU name — which starts with the flavor. That is
 * how "750ml / 12pk" ended up printed once per flavor. Each tier therefore
 * falls back to comparing the segment itself before the full name.
 *
 * @param {string[]} skus
 * @param {{sizeOrder?: string[], packOrder?: string[]}} order the account's own ordering
 */
export function sortSkus(skus, order = {}) {
  return [...(skus || [])].sort((a, b) => {
    const [as, ap] = pkgRank(a, order);
    const [bs, bp] = pkgRank(b, order);
    if (as !== bs) return as - bs;
    const sizeCmp = getSkuSize(a).localeCompare(getSkuSize(b));
    if (sizeCmp !== 0) return sizeCmp;
    if (ap !== bp) return ap - bp;
    const packCmp = getSkuPkg(a).localeCompare(getSkuPkg(b));
    if (packCmp !== 0) return packCmp;
    return String(a).localeCompare(String(b));
  });
}
